import React, { useMemo } from "react";

/**
 * Ghost — the Apiboo mascot mark used as the auth modal logo.
 * Flat, minimal, soft fully-rounded body with round white eyes.
 * Supports an idle animation (slow float + liquid wobble + glance + blink),
 * gated to `prefers-reduced-motion`. Animation keyframes live in auth-modal.css.
 *
 * Props:
 *   variant  'classic' | 'gradient' | 'pop' | 'outline'   (default 'gradient')
 *   size     px width (height is size * 1.2)              (default 58)
 *   animated run the idle animation                       (default false)
 *   glow     draw the soft floor shadow                   (default false)
 *   mono     force a single colour (e.g. '#fff')          (default null)
 *   eyeColor override eye fill                            (default null)
 */

export const AP = {
  purple: "#8b5cf6",
  purpleSoft: "#a78bfa",
  purpleDeep: "#6d3df2",
  ink: "#0d0c14",
  ink0: "#08070d",
  white: "#f4f3f7",
};

// smooth closed spline (Catmull-Rom → cubic Bézier)
function catmullClosed(pts) {
  const n = pts.length;
  const P = (i) => pts[((i % n) + n) % n];
  const f = (v) => v.toFixed(2);
  let d = `M ${f(P(0)[0])} ${f(P(0)[1])} `;
  for (let i = 0; i < n; i++) {
    const p0 = P(i - 1), p1 = P(i), p2 = P(i + 1), p3 = P(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += `C ${f(c1x)} ${f(c1y)}, ${f(c2x)} ${f(c2y)}, ${f(p2[0])} ${f(p2[1])} `;
  }
  return d + "Z";
}

function ghostPoints({ lx, rx, topY, sideBottomY, baseY, amp, bumps, ampArr = null }) {
  const cx = (lx + rx) / 2, w = rx - lx, h = sideBottomY - topY;
  const pts = [
    [lx, sideBottomY],
    [lx, topY + h * 0.46],
    [lx + w * 0.13, topY + h * 0.12],
    [cx, topY],
    [rx - w * 0.13, topY + h * 0.12],
    [rx, topY + h * 0.46],
    [rx, sideBottomY],
  ];
  const seg = (rx - lx) / bumps;
  for (let i = 0; i < bumps; i++) {
    const a = (ampArr ? ampArr[i] : 1) * amp;
    pts.push([rx - seg * (i + 0.5), baseY + a]);
    if (i < bumps - 1) pts.push([rx - seg * (i + 1), baseY - amp * 0.12]);
  }
  return pts;
}

const VAR = {
  classic: { body: { lx: 16, rx: 104, topY: 15, sideBottomY: 97, baseY: 99, amp: 11, bumps: 3 }, eye: { r: 10, cy: 55, cxL: 47, cxR: 73 } },
  gradient: { body: { lx: 13, rx: 107, topY: 12, sideBottomY: 100, baseY: 102, amp: 13, bumps: 3 }, eye: { r: 10.5, cy: 55, cxL: 46, cxR: 74 } },
  pop: { body: { lx: 16, rx: 104, topY: 15, sideBottomY: 97, baseY: 99, amp: 11, bumps: 3 }, eye: { r: 10, cy: 55, cxL: 47, cxR: 73 } },
  outline: { body: { lx: 19, rx: 101, topY: 16, sideBottomY: 96, baseY: 98, amp: 9, bumps: 3 }, eye: { r: 8, cy: 55, cxL: 48, cxR: 72 } },
};

function morphFrame(b, { sx = 1, sy = 1, shear = 0, amps = null } = {}) {
  const cx = (b.lx + b.rx) / 2;
  const groundY = b.baseY + b.amp;
  const pts = ghostPoints({ ...b, ampArr: amps }).map(([x, y]) => {
    const hAbove = groundY - y;
    return [cx + (x - cx) * sx + shear * (hAbove / groundY), groundY - hAbove * sy];
  });
  return catmullClosed(pts);
}

function bodyMorphValues(b) {
  const f = (o) => morphFrame(b, o);
  return [
    f({}),
    f({ sx: 1.04, sy: 0.95, amps: [1.18, 0.82, 1.14] }),
    f({ sx: 0.97, sy: 1.04, shear: 2.6, amps: [0.85, 1.15, 0.88] }),
    f({ sx: 1.025, sy: 0.98, shear: -2, amps: [1.1, 0.88, 1.05] }),
    f({}),
  ].join(";");
}

export default function Ghost({
  variant = "gradient",
  size = 58,
  animated = false,
  glow = false,
  mono = null,
  eyeColor = null,
  style = {},
}) {
  const uid = useMemo(() => "g" + Math.random().toString(36).slice(2, 8), []);
  const cfg = VAR[variant] || VAR.classic;
  const isMono = !!mono;

  let bodyFill, eyeFill, bodyStroke = "none", strokeW = 0, defs = null;
  if (isMono) {
    const lightOnDark = mono === "#fff" || mono === AP.white;
    bodyFill = variant === "outline" ? "none" : mono;
    bodyStroke = variant === "outline" ? mono : "none";
    strokeW = variant === "outline" ? 5 : 0;
    eyeFill = variant === "outline" ? mono : lightOnDark ? "var(--accent-hover)" : "#fff";
  } else if (variant === "classic") {
    bodyFill = "var(--accent)"; eyeFill = "var(--ghost-eye-fill, #fff)";
  } else if (variant === "gradient") {
    bodyFill = `url(#${uid}grad)`; eyeFill = "var(--ghost-eye-fill, #fff)";
    defs = (
      <linearGradient id={`${uid}grad`} x1="0" y1="0" x2="0.25" y2="1">
        <stop offset="0" style={{ stopColor: "var(--accent-soft)" }} />
        <stop offset="1" style={{ stopColor: "var(--accent-hover)" }} />
      </linearGradient>
    );
  } else if (variant === "pop") {
    bodyFill = "#fff"; eyeFill = "var(--accent-hover)";
  } else if (variant === "outline") {
    bodyFill = "none"; bodyStroke = "var(--accent)"; strokeW = 5; eyeFill = "var(--accent)";
  }
  if (eyeColor) eyeFill = eyeColor;

  const e = cfg.eye, b = cfg.body;
  const rest = catmullClosed(ghostPoints(b));
  const morphVals = animated ? bodyMorphValues(b) : null;
  const SPL = "0.45 0 0.55 1";

  const eyes = (
    <>
      <circle className={animated ? "apb-blink" : undefined} cx={e.cxL} cy={e.cy} r={e.r} fill={eyeFill}
        style={{ transformBox: "fill-box", transformOrigin: "center" }} />
      <circle className={animated ? "apb-blink" : undefined} cx={e.cxR} cy={e.cy} r={e.r} fill={eyeFill}
        style={{ transformBox: "fill-box", transformOrigin: "center" }} />
    </>
  );

  const facePart = !animated ? eyes : (
    <g className="apb-eyebob"><g className="apb-look">{eyes}</g></g>
  );

  const inner = (
    <>
      <path d={rest} fill={bodyFill} stroke={bodyStroke} strokeWidth={strokeW} strokeLinejoin="round">
        {animated && (
          <animate attributeName="d" dur="6.5s" repeatCount="indefinite"
            values={morphVals} calcMode="spline" keyTimes="0;0.25;0.5;0.75;1"
            keySplines={`${SPL};${SPL};${SPL};${SPL}`} />
        )}
      </path>
      {facePart}
    </>
  );

  return (
    <svg viewBox="0 0 120 144" width={size} height={size * 1.2}
      style={{ overflow: "visible", display: "block", ...style }}>
      {defs && <defs>{defs}</defs>}
      {glow && (
        <ellipse className={animated ? "apb-shadow" : undefined} cx="60" cy="139" rx="33" ry="6"
          fill={AP.ink0} opacity="0.3" style={{ transformBox: "fill-box", transformOrigin: "center" }} />
      )}
      {animated ? <g className="apb-float">{inner}</g> : <g>{inner}</g>}
    </svg>
  );
}
